import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faCalendarDays,
  faFont,
  faICursor,
  faSignature,
} from "@fortawesome/free-solid-svg-icons";
import { SignatureRequest } from "../../../../../interfaces/file/fileSignature/SignatureRequest";
import { SignatureType } from "../../../../../interfaces/file/fileSignature/SignatureBox";

interface GuestSidebarProps {
  activeNav: string | null;
  toggleNav: (nav: string) => void;
  requestData: SignatureRequest;
  handleSelectBox: (boxId: string) => void;
  selectedBoxId: string | null;
  /**
   * Called when the 'Sign' button is clicked.
   * Should trigger the UI to allow the user to sign the specified box.
   */
  onSign: (boxId: string) => void;
  /**
   * Called when the 'Finish & Sign' button is clicked.
   * Should handle signing the final box and submitting the document.
   * Should return a promise to manage loading state.
   */
  onFinishAndSign: (boxId: string) => Promise<void>;
}

const GuestSidebar: React.FC<GuestSidebarProps> = ({
  activeNav,
  toggleNav,
  requestData,
  handleSelectBox,
  selectedBoxId,
  onSign,
  onFinishAndSign,
}) => {
  const [signingStarted, setSigningStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const userSignatureBoxes = useMemo(() => {
    return requestData.signatureBoxes
      .filter(
        (box) =>
          box.assignedSignerEmail.toLowerCase() ===
          requestData.signerEmail.toLowerCase()
      )
      .sort((a, b) => a.pageNumber - b.pageNumber || a.y - b.y);
  }, [requestData.signatureBoxes, requestData.signerEmail]);

  const unsignedUserSignatureBoxes = useMemo(
    () => userSignatureBoxes.filter((box) => !box.signatureValue?.trim()),
    [userSignatureBoxes]
  );

  const selectedBoxIndexInUnsigned = unsignedUserSignatureBoxes.findIndex(
    (b) => b.id === selectedBoxId
  );

  const isOnlyOneUnsignedBoxLeft = unsignedUserSignatureBoxes.length === 1;
  const isUnsignedBoxSelected = selectedBoxIndexInUnsigned !== -1;

  const handleStart = () => {
    setSigningStarted(true);
    if (unsignedUserSignatureBoxes.length > 0) {
      handleSelectBox(unsignedUserSignatureBoxes[0].id);
    }
  };

  const handleNext = () => {
    if (!userSignatureBoxes || userSignatureBoxes.length === 0 || unsignedUserSignatureBoxes.length === 0) {
      return;
    }
  
    const currentIndex = userSignatureBoxes.findIndex(b => b.id === selectedBoxId);
  
    // If no box is selected, or if the selected box isn't found, default to the first unsigned box
    if (currentIndex === -1) {
      handleSelectBox(unsignedUserSignatureBoxes[0].id);
      return;
    }
  
    // Start searching from the box after the current one, looping around the entire list
    for (let i = 1; i <= userSignatureBoxes.length; i++) {
      const nextIndex = (currentIndex + i) % userSignatureBoxes.length;
      const nextBox = userSignatureBoxes[nextIndex];
  
      // Check if this box is in our list of unsigned boxes
      if (unsignedUserSignatureBoxes.some(unsignedBox => unsignedBox.id === nextBox.id)) {
        handleSelectBox(nextBox.id);
        return; // Exit after finding the next unsigned box
      }
    }
  };

  const handleSign = () => {
    if (selectedBoxId) {
      onSign(selectedBoxId);
    }
  };

  const handleFinish = async () => {
    if (selectedBoxId) {
      setIsLoading(true);
      await onFinishAndSign(selectedBoxId);
      // The parent component will render SuccessView, so no need to toggle loading state back.
    }
  };

  const groupedSignatureBoxes = useMemo(() => {
    return userSignatureBoxes.reduce((acc, box) => {
      const page = box.pageNumber;
      if (!acc[page]) {
        acc[page] = [];
      }
      acc[page].push(box);
      return acc;
    }, {} as { [key: number]: typeof userSignatureBoxes });
  }, [userSignatureBoxes]);

  return (
    <div className="sign_right">
      <div className="common_card h-100 d-flex flex-column">
        <div className="flex-grow-1">
          <div
            className="top top_bar_flx border_tittle cursor-pointer"
            onClick={() => toggleNav("document")}
          >
            <div className="top_bar_tittle">
              <h2>Document</h2>
            </div>
            <FontAwesomeIcon
              icon={activeNav === "document" ? faChevronDown : faChevronRight}
            />
          </div>
          {activeNav === "document" && (
            <div className="document_stats_grid mb-3">
              <div className="stats">
                <h3>Assigned Signer</h3>
                <p>{requestData.signerEmail}</p>
              </div>
            </div>
          )}
          <div
            className="top top_bar_flx border_tittle cursor-pointer"
            onClick={() => toggleNav("signature-boxes")}
          >
            <div className="top_bar_tittle">
              <h2 className="font-bold">Signature Boxes</h2>
            </div>
            <FontAwesomeIcon
              icon={
                activeNav === "signature-boxes"
                  ? faChevronDown
                  : faChevronRight
              }
            />
          </div>
          {activeNav === "signature-boxes" && (
            <div className="mb-3">
              {Object.keys(groupedSignatureBoxes)
                .sort((a, b) => parseInt(a) - parseInt(b))
                .map((pageNumberStr) => {
                  const pageNumber = parseInt(pageNumberStr);
                  const fieldsOnPage = groupedSignatureBoxes[pageNumber];
                  return (
                    <div key={pageNumber}>
                      <div className="flex items-center mt-4 mb-2">
                        <h4 className="flex-shrink-0 font-semibold text-xs text-muted mr-4">
                          Page {pageNumber}
                        </h4>
                        <div className="flex-grow border-b border-muted"></div>
                      </div>
                      {fieldsOnPage.map((box) => {
                        const isSigned = !!box.signatureValue;
                        return (
                          <div
                            key={box.id}
                            onClick={() => handleSelectBox(box.id)}
                            className={` mb-2 p-2 rounded cursor-pointer transition-colors ${
                              box.id === selectedBoxId
                                ? "border border-purple-500 bg-purple-100"
                                : isSigned
                                ? "border border-green-200 bg-green-100"
                                : "border border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <div className="flex align-middle items-center gap-2">
                              {box.boxType.name === SignatureType.DATE && (
                                <FontAwesomeIcon
                                  className="w-6"
                                  icon={faCalendarDays}
                                />
                              )}
                              {box.boxType.name === SignatureType.SIGNATURE && (
                                <FontAwesomeIcon
                                  className="w-6"
                                  icon={faSignature}
                                />
                              )}
                              {box.boxType.name === SignatureType.INITIALS && (
                                <FontAwesomeIcon
                                  className="w-6"
                                  icon={faFont}
                                />
                              )}
                              {box.boxType.name === SignatureType.TEXT && (
                                <FontAwesomeIcon
                                  className="w-6"
                                  icon={faICursor}
                                />
                              )}
                              <div className="text_turcate">
                                {"You"}
                                {isSigned ? " (Completed)" : " (Required)"}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              {userSignatureBoxes.length === 0 && (
                <div className="stats">
                  <p>There are no signature fields assigned to you.</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-auto p-3 border-top">
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {!signingStarted && (
                <button
                  className="btn btn-primary w-100"
                  onClick={handleStart}
                  disabled={unsignedUserSignatureBoxes.length === 0}
                >
                  Start
                </button>
              )}
              {signingStarted && unsignedUserSignatureBoxes.length > 0 && (
                <div className="d-grid gap-2">
                  {isUnsignedBoxSelected ? (
                    // An unsigned box is selected
                    isOnlyOneUnsignedBoxLeft ? (
                      <button
                        className="btn btn-success w-100"
                        onClick={handleFinish}
                      >
                        Finish & Sign
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-primary w-100"
                          onClick={handleSign}
                        >
                          Sign
                        </button>
                        <button
                          className="btn btn-secondary w-100"
                          onClick={handleNext}
                        >
                          Next Signature
                        </button>
                      </>
                    )
                  ) : (
                    // A signed box is selected
                    selectedBoxId && (
                      <button
                        className="btn btn-secondary w-100"
                        onClick={handleNext}
                      >
                        Next Signature
                      </button>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestSidebar;
